import { useEffect } from 'react';
import { KNOWLEDGE_ARTICLES } from '../data/knowledgeArticles';
import { SERVICES } from '../data/staticData';

export const DOMAIN = 'https://www.dansktagbyg.dk';

// 15 primære faste sider
export const PRIMARY_STATIC_ROUTES = {
  '/': `${DOMAIN}/`,
  '/nyt-tag': `${DOMAIN}/nyt-tag`,
  '/processen': `${DOMAIN}/processen`,
  '/asbest-autoriseret': `${DOMAIN}/asbest-autoriseret`,
  '/ydelser': `${DOMAIN}/ydelser`,
  '/energiloesninger': `${DOMAIN}/energiloesninger`,
  '/viden': `${DOMAIN}/viden`,
  '/projekter': `${DOMAIN}/projekter`,
  '/anmeldelser': `${DOMAIN}/anmeldelser`,
  '/profil': `${DOMAIN}/profil`,
  '/garantier': `${DOMAIN}/garantier`,
  '/job': `${DOMAIN}/job`,
  '/kontakt': `${DOMAIN}/kontakt`,
  '/prisberegner': `${DOMAIN}/prisberegner`,
  '/prisberegner-energiloesninger': `${DOMAIN}/prisberegner-energiloesninger`,
};

// 10 primære områdesider under /p/nyt-tag/:slug
export const PRIMARY_AREA_SLUGS = new Set([
  'erfaren-tagdaekker-i-aarhus',
  'nyt-tag-i-randers',
  'horsens',
  'skanderborg',
  'djursland',
  'odder',
  'nyt-tag-i-hinnerup',
  'nyt-tag-i-hoejbjerg',
  'risskov',
  'nyt-tag-i-hammel',
]);

// 13 primære ydelsessider under /ydelser/:slug
export const PRIMARY_SERVICE_SLUGS = new Set(
  SERVICES.map((s) => s.slug).filter(Boolean)
);

// 26 primære vidensartikler under /viden/:slug
export const PRIMARY_KNOWLEDGE_SLUGS = new Set(
  KNOWLEDGE_ARTICLES.map((a) => a.slug).filter(Boolean)
);

/**
 * Returnerer self-referencing canonical URL hvis stien tilhører en godkendt primær side,
 * eller null hvis siden er en kopi, redirect, fejlsiden eller ukendt.
 */
export function getCanonicalForPath(pathname) {
  if (!pathname) return null;

  // Normalisér trailing slash (undtagen forside '/')
  const cleanPath = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;

  // 1. Tjek faste primære sider
  if (PRIMARY_STATIC_ROUTES[cleanPath]) {
    return PRIMARY_STATIC_ROUTES[cleanPath];
  }

  // 2. Tjek primære områdesider under /p/nyt-tag/:slug
  // (Bemærk: /nyt-tag/:area er kopier og returnerer null)
  if (cleanPath.startsWith('/p/nyt-tag/')) {
    const slug = cleanPath.replace('/p/nyt-tag/', '');
    if (PRIMARY_AREA_SLUGS.has(slug)) {
      return `${DOMAIN}/p/nyt-tag/${slug}`;
    }
  }

  // 3. Tjek primære ydelser under /ydelser/:slug
  // (Bemærk: /c/ydelser/:slug er kopier og returnerer null)
  if (cleanPath.startsWith('/ydelser/')) {
    const slug = cleanPath.replace('/ydelser/', '');
    if (PRIMARY_SERVICE_SLUGS.has(slug)) {
      return `${DOMAIN}/ydelser/${slug}`;
    }
  }

  // 4. Tjek primære vidensartikler under /viden/:slug
  // (Bemærk: /c/viden/:slug er kopier og returnerer null)
  if (cleanPath.startsWith('/viden/')) {
    const slug = cleanPath.replace('/viden/', '');
    if (PRIMARY_KNOWLEDGE_SLUGS.has(slug)) {
      return `${DOMAIN}/viden/${slug}`;
    }
  }

  // Alle kopier, redirects (/p/nyt-tag/beregn-din-pris osv.), /403 og 404 får intet canonical tag
  return null;
}

/**
 * Indsætter, opdaterer eller fjerner <link rel="canonical"> i document.head
 */
export function setCanonicalTag(url) {
  if (typeof document === 'undefined') return;

  let link = document.querySelector('link[rel="canonical"]');

  if (url) {
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  } else if (link) {
    link.remove();
  }
}

/**
 * React hook til at sætte canonical tag på en side.
 * Kan bruges i fremtidige nye sider hvis de skal definere deres egen canonical eksplicit:
 * useCanonical('https://www.dansktagbyg.dk/min-nye-side')
 */
export function useCanonical(customUrl) {
  useEffect(() => {
    if (customUrl) {
      setCanonicalTag(customUrl);
    }
  }, [customUrl]);
}
